<template>
  <BaseInput class="summary" :label="''" @click="showSummary = !showSummary">
    <template #label>
      <span class="summary-title">
        <p>{{ $t('OrderSummary.label.summary') }}</p>
        <p class="show-summary-text" :class="{ isOpen: showSummary }">
          {{ !showSummary ? $t('OrderSummary.label.show') : $t('OrderSummary.label.hide') }} {{ $t('OrderSummary.label.fullSummary') }}

          <CaretDown isInverseColor />
        </p>
      </span>
    </template>

    <div v-if="feeData.isLoading" class="skele-loader">
      <SkeleLoader :lines="1" contentIsFullWidth />
    </div>

    <!-- show payable row first -->
    <div class="two-column-table" v-if="!feeData.isLoading && !showSummary">
      <ul>
        <li>
          <p v-html="showPayableRow.label"></p>
          <div>
            <p v-html="showPayableRow.value"></p>
          </div>
        </li>
      </ul>
    </div>

    <DetailsToggle :showSummary="showSummary">
      <div class="two-column-table">
        <ul>
          <li v-for="row in handleSummaryData" :key="row">
            <template v-if="feeData.isLoading">
              <SkeleLoader :lines="1" contentIsFullWidth :key="row" />
            </template>

            <template v-else>
              <p v-html="row.label"></p>
              <div>
                <p v-html="row.value"></p>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </DetailsToggle>
  </BaseInput>
</template>

<script setup>
import { computed, ref } from "vue";

import useDecimalHelpers from "@/composables/helpers/useDecimalHelpers";
import { toCurrencyFormat } from "@/composables/helpers/useStringHelpers";

import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import DetailsToggle from "@/components/ui/toggles/DetailsToggle.vue";
import CaretDown from "@/components/icons/CaretDown.vue";
import { useI18n } from 'vue-i18n';
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const props = defineProps({
  feeData: {
    type: Object,
    required: true,
  },
  formData: {
    type: Object,
    required: true,
  },
});

const { convertCoinAmountToDecimals } = useDecimalHelpers;
const showSummary = ref(false);
const { t } = useI18n();

const convertCoinToDecimals = computed(
  () =>
    !props.feeData.isLoading &&
    convertCoinAmountToDecimals(props.formData.coinAmount.value, 8)
);

/**
 * Currently used in BuyForm using the Buy Store's FeeData and Form
 */

const handleSummaryData = computed(() => {
  const { networkFee, gatewayFee, receivedRate, quoteValue } = props.feeData;
  const { coinType, fiatType, fiatAmount } = props.formData;
  const currency = fiatType.value;

  const tabledata = {
    orderValue: {
      label: `${convertCoinToDecimals.value} ${
        coinType.value
      } <small>(@${parseFloat(receivedRate).toFixed(2)} ${currency})</small>`,
      value: `${toCurrencyFormat(quoteValue, currency)} ${currency}`,
    },
    gateway: {
      label: `${t('OrderSummary.label.gatewayFee')}`,
      value: `${toCurrencyFormat(gatewayFee, currency)} ${currency}`,
    },
    network: {
      label: `${t('OrderSummary.label.networkFee')}`,
      value: `${toCurrencyFormat(networkFee, currency)} ${currency}`,
    },
    payable: {
      label: `<strong>${t('OrderSummary.label.totalPayable')}</strong>`,
      value: `${toCurrencyFormat(fiatAmount.value, currency)} ${currency}`,
    },
  };

  return tabledata;
});

const showPayableRow = computed(() => {
  const { networkFee, gatewayFee } = props.feeData;
  const { fiatType } = props.formData;
  const currency = fiatType.value;
  const totalFees = parseFloat(networkFee) + parseFloat(gatewayFee);

  return {
    label: handleSummaryData.value.payable.label,
    value: `${handleSummaryData.value.payable.value} (incl. ${toCurrencyFormat(
      totalFees,
      currency
    )} fees)`,
  };
});
</script>

<style lang="scss" scoped>
.skele-loader {
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
  .skeleton {
    width: 100%;
  }
}
.summary {
  cursor: pointer;
}

.summary-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0rem 1rem;

  .show-summary-text {
    margin-right: 30px; // account for absolute pos caret
    color: var(--input-place-holder-text-color);

    &.isOpen {
      svg.caret-down {
        transform: rotate(180deg) translate(50%, 50%);
      }
    }
  }

  svg.caret-down {
    right: 15px;
    top: 12px;
    transform: rotate(0deg) translate(-50%, -50%);
    transition: transform 250ms;
    path {
      color: var(--input-place-holder-text-color);
    }
  }
}
</style>
